import { usageTypeIcons } from "../lib/connector";
import { pluralize } from "../lib/inflection";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { FacePile } from "./FacePile";
import { Icon, type IconName } from "./Icon";
import { Tooltip } from "./Tooltip";

const usageEntries = [
  { type: "personal_chat", noun: "chat" },
  { type: "space_git_remote", noun: "git sync" },
  { type: "audit_log_destination", noun: "audit log destination" },
] as const;

export const displayedUsageTypes: ReadonlySet<string> = new Set(
  usageEntries.map((e) => e.type)
);

export function ConnectorUsageIcons({
  workflowCount,
  usages,
  collapseToFit = false,
}: {
  // Workflows with a live branch head running the connector. A workflow_slug
  // row in `usages` is a name reservation, not use, so it isn't counted.
  workflowCount: number;
  usages: readonly { type: string }[];
  collapseToFit?: boolean;
}) {
  const counts = new Map<string, number>();
  for (const usage of usages)
    if (displayedUsageTypes.has(usage.type))
      counts.set(usage.type, (counts.get(usage.type) ?? 0) + 1);

  const all: { id: string; icon: IconName; count: number; noun: string }[] = [
    {
      id: "workflows",
      icon: usageTypeIcons.workflow,
      count: workflowCount,
      noun: "workflow",
    },
    ...usageEntries.map(({ type, noun }) => ({
      id: type,
      icon: usageTypeIcons[type],
      count: counts.get(type) ?? 0,
      noun,
    })),
  ];
  const entries = all.filter((entry) => entry.count > 0);
  if (entries.length === 0) return null;

  return (
    <FacePile
      itemSize={24}
      outlineWidth={2}
      cutout
      collapseToFit={collapseToFit}
      items={entries.map((entry) => {
        const label = `${entry.count} ${pluralize(entry.noun, entry.count)}`;
        return {
          id: entry.id,
          label,
          bare: true,
          node: (
            <Tooltip content={label}>
              <ConnectorLogoContainer
                size={24}
                outline={false}
                outlineWidth={2}
              >
                <Icon
                  name={entry.icon}
                  size={12}
                  color="var(--text-secondary)"
                />
              </ConnectorLogoContainer>
            </Tooltip>
          ),
        };
      })}
    />
  );
}
